/**
 * @file pwa/pending-upload-worker.ts
 * @description 后台离线上传 worker。
 *
 * 触发时机（多重保险，全部最终调用 flushPendingUploads）：
 *  - window 'online' 事件：网络恢复立即跑
 *  - useOfflineState 的同步周期内（pwa/sync.ts）调用一次
 *  - 用户手动「立即同步」按钮
 *  - 进入 form fill 页面 useEffect 兜底
 *
 * 单条 blob 流程：
 *   markUploading → uploadFile（presign+PUT+confirm）
 *     → 成功：markUploaded(realId) + replaceTempIdInOutbox + 广播 replaced 事件
 *     → 失败：markError（retryCount++）；retryCount≥3 后跳过
 *
 * 顺序处理（不并发）以避免占满网络。
 */
import { uploadFile } from '@/lib/file-upload';
import { getOfflineDb } from '@/pwa/db';
import {
  listPending,
  markUploading,
  markUploaded,
  markError,
  replaceTempIdInOutbox,
} from '@/pwa/pending-uploads';

/** 重试上限：超过后停在 error 状态等用户手动重试 */
const MAX_RETRY = 3;

/** 单例 flush 锁，防止并发 */
let flushing: Promise<void> | null = null;

/**
 * 在浏览器中安全广播 'pending-upload:replaced'；node/test 环境下静默跳过。
 *
 * 单独抽出来，避免 dispatchEvent 抛出（如 SSR/node）污染上传成功路径，
 * 否则会被外层 catch 误判为上传失败 → markError → retryCount 自增。
 */
function broadcastReplaced(tempId: number, realId: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent('pending-upload:replaced', {
        detail: { tempId, realId },
      }),
    );
  } catch (err) {
    console.warn('[pending-upload-worker] dispatch broadcast failed', err);
  }
}

/**
 * 处理单条暂存 blob：markUploading → uploadFile → markUploaded → outbox 替换 + 广播。
 * 失败统一走 markError，调用方据状态机决定后续重试。
 */
async function processRow(row: {
  id: string;
  blob: Blob;
  ownerType?: string;
  ownerId?: number;
  filename: string;
  mimeType: string;
}): Promise<void> {
  const tempId = Number(row.id.replace(/^pending:/, ''));
  if (!Number.isFinite(tempId) || tempId >= 0) return;

  await markUploading(tempId);
  try {
    const uploaded = await uploadFile(row.blob, {
      scope: 'form-attachment',
      ownerType: row.ownerType,
      ownerId: row.ownerId,
      filename: row.filename,
      mimeType: row.mimeType,
    });
    await markUploaded(tempId, uploaded.id);
    await replaceTempIdInOutbox(tempId, uploaded.id);
    broadcastReplaced(tempId, uploaded.id);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[pending-upload-worker] upload failed', tempId, err);
    await markError(tempId, msg);
  }
}

/**
 * 一次性 flush：扫所有 pending（且 retryCount < MAX_RETRY）的 blob，顺序上传。
 *
 * 幂等：并发调用合并到同一个 in-flight Promise。
 *
 * 实现要点：先快照 errored 列表，再处理 pending；保证同一次 flush 内
 * 「刚刚失败」的行不会被立刻 retry（避免一次 flush 把 retryCount 烧光）。
 */
export function flushPendingUploads(): Promise<void> {
  if (flushing) return flushing;
  flushing = (async () => {
    try {
      const db = await getOfflineDb();

      // 先快照可重试的 errored 行（必须在处理 pending 之前取，
      // 否则刚由 pending → error 的行会在同一次 flush 内被立即重试）
      const erroredSnapshot = await db.getAllFromIndex('cache_media', 'by-status', 'error');

      // 1) 处理所有 pending（status='pending'）行
      const pending = await listPending();
      for (const row of pending) {
        await processRow(row);
      }

      // 2) 处理快照中的 errored 行（retryCount < MAX_RETRY 才重试）
      for (const row of erroredSnapshot) {
        if ((row.retryCount ?? 0) >= MAX_RETRY) continue;
        await processRow(row);
      }
    } finally {
      flushing = null;
    }
  })();
  return flushing;
}

/**
 * 启动 online 事件监听，网络恢复时自动 flush。
 * 多次调用安全（addEventListener 重复注册无副作用，但仍只挂一次）。
 */
let listenerAttached = false;
export function startPendingUploadWorker(): void {
  if (listenerAttached) return;
  listenerAttached = true;
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => {
    void flushPendingUploads();
  });
}
